/**
 * Line diff between two prompt versions.
 *
 * Written rather than pulled in: this repo keeps a deliberately small runtime
 * dependency list (7 packages), and a line-level LCS diff is short enough that
 * a dependency would cost more than it saves.
 *
 * Line-level, not word-level, on purpose. Prompts are instructions written one
 * rule per line, and what an operator needs to see is which rules changed —
 * a word-level diff of a 6.8 KB prompt produces speckle that is harder to read
 * than the prompt itself.
 */

export type DiffType = 'context' | 'added' | 'removed';

export interface DiffRow {
  type: DiffType;
  text: string;
  /** 1-based line number in the left/right side, absent where the row doesn't exist. */
  leftLine?: number;
  rightLine?: number;
}

export interface DiffResult {
  rows: DiffRow[];
  added: number;
  removed: number;
  /** True when the inputs were too large to diff and were compared wholesale. */
  truncated: boolean;
}

/**
 * Above this, the O(n×m) table stops being worth building. The largest prompt
 * in the catalog is ~6.8 KB (~120 lines), so this is far above anything real —
 * it exists so a pathological paste can't hang the browser tab.
 */
const MAX_LINES = 3000;

/** Longest-common-subsequence table over lines. */
function lcsLengths(a: string[], b: string[]): number[][] {
  const table: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0),
  );

  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      table[i][j] =
        a[i] === b[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  return table;
}

/**
 * Compare `left` (what is live) against `right` (the candidate).
 *
 * `added` means present in `right` only; `removed` means present in `left` only.
 */
export function diffLines(left: string, right: string): DiffResult {
  const a = left.split('\n');
  const b = right.split('\n');

  if (a.length > MAX_LINES || b.length > MAX_LINES) {
    return {
      rows: [
        ...a.map((text, i) => ({ type: 'removed' as const, text, leftLine: i + 1 })),
        ...b.map((text, i) => ({ type: 'added' as const, text, rightLine: i + 1 })),
      ],
      added: b.length,
      removed: a.length,
      truncated: true,
    };
  }

  const table = lcsLengths(a, b);
  const rows: DiffRow[] = [];
  let i = 0;
  let j = 0;
  let added = 0;
  let removed = 0;

  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      rows.push({ type: 'context', text: a[i], leftLine: i + 1, rightLine: j + 1 });
      i++;
      j++;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      rows.push({ type: 'removed', text: a[i], leftLine: i + 1 });
      removed++;
      i++;
    } else {
      rows.push({ type: 'added', text: b[j], rightLine: j + 1 });
      added++;
      j++;
    }
  }
  while (i < a.length) {
    rows.push({ type: 'removed', text: a[i], leftLine: i + 1 });
    removed++;
    i++;
  }
  while (j < b.length) {
    rows.push({ type: 'added', text: b[j], rightLine: j + 1 });
    added++;
    j++;
  }

  return { rows, added, removed, truncated: false };
}

/**
 * Drop long runs of unchanged lines, keeping `padding` either side of a change.
 *
 * Without this the diff of a 120-line prompt with one edited rule is 120 rows
 * of which 2 matter. Returns rows plus `gap` markers naming how many lines were
 * hidden, so nothing silently disappears.
 */
export interface CollapsedGap {
  type: 'gap';
  hidden: number;
}

export type CollapsedRow = DiffRow | CollapsedGap;

export function collapseUnchanged(rows: DiffRow[], padding = 3): CollapsedRow[] {
  const keep = new Array<boolean>(rows.length).fill(false);

  rows.forEach((row, index) => {
    if (row.type === 'context') return;
    for (let k = Math.max(0, index - padding); k <= Math.min(rows.length - 1, index + padding); k++) {
      keep[k] = true;
    }
  });

  const out: CollapsedRow[] = [];
  let hidden = 0;

  rows.forEach((row, index) => {
    if (keep[index]) {
      if (hidden > 0) {
        out.push({ type: 'gap', hidden });
        hidden = 0;
      }
      out.push(row);
    } else {
      hidden++;
    }
  });
  if (hidden > 0) out.push({ type: 'gap', hidden });

  return out;
}

export function isGap(row: CollapsedRow): row is CollapsedGap {
  return (row as CollapsedGap).type === 'gap';
}
